import {get, post} from "../../api";
import {TeamsGetTeamsChannelsRequest as TeamsGetTeamsChannelsRequest} from "./TeamsGetTeamsChannelsRequest";
import {TeamsGetExternalTeamNameRequest} from "./TeamsGetExternalTeamNameRequest";
import {TeamsGetArchivedStatusRequest} from "./TeamsGetArchivedStatusRequest";
import {TeamsGetRecentDocumentsRequest} from "./TeamsGetRecentDocumentsRequest";
import {TeamsGetTeamChannelsRequest} from "./TeamsGetTeamChannelsRequest";
import {NotFoundWebError} from "../../errors/NotFoundWebError";
import {ForbiddenWebError} from "../../errors/ForbiddenWebError";
import {FilingLocation} from "../../models";
import {IDropdownOption} from "office-ui-fabric-react";
import { ErrorHandler } from "../../errors/ErrorHandler";

export class TeamsApiService {
    errorHandler = new ErrorHandler();

    public async getTeamsRecentDocuments(
        request: TeamsGetRecentDocumentsRequest,
        defaultErrorMessage: string,
        onSuccess: (response: FilingLocation[]) => void,
        onError: (errorMessage: string) => void
    ) {
        return this.makeApiCall(
            post("/api/teamsdmsintegration/GetTeamsRecentDocuments", {
                TeamsId: request.teamId,
                ChannelId: request.channelId,
                RecentDocs: request.recentDocs,
                StorageType: request.storageType,
                TenantId: request.tenantId,
                UserId: request.userId,
            }),
            defaultErrorMessage,
            onSuccess,
            onError
        );
    }

    private async makeApiCall<P>(
        apiCall: Promise<Response>,
        defaultErrorMessage: string,
        onSuccess: (response: P) => void,
        onError: (errorMessage: string, errorStatusCode: string) => void
    ) {
        apiCall
            .then((response) => this.getResponseJson(response, defaultErrorMessage))
            .then((responseJson) => onSuccess(responseJson))
            .catch((error) => this.handleError(error, onError));
    }

    private async getResponseJson(response: Response, defaultErrorMessage: string) {
        const statusCode = response.status.toString();
        if (statusCode.startsWith("2")) {
            return response.json();
        } else if (statusCode == "404") {
            const json = await response.json();
            this.errorHandler.HandleConfigurationErrorResponse(json, defaultErrorMessage, (errorMessage: string) => {
                throw new NotFoundWebError(errorMessage, json.ErrorCode);
            });
        } else if (statusCode == "403") {
            const json = await response.json();
            this.errorHandler.HandleConfigurationErrorResponse(json, defaultErrorMessage, (errorMessage: string) => {
                throw new ForbiddenWebError(errorMessage, statusCode);
            });
        } else {
            const json = await response.json();
            this.errorHandler.HandleConfigurationErrorResponse(json, defaultErrorMessage, (errorMessage: string) => {
                throw new Error(errorMessage);
            });
        }
    }

    private handleError(
        error: Error,
        onError: (errorMessage: string, errorStatusCode: string) => void
    ) {
        if (error instanceof NotFoundWebError) {
            onError(error.message, "");
        } else if (error instanceof ForbiddenWebError) {
            onError(error.message, error.errorCode);
        } else {
            console.error(error, error.stack);
            onError(error.message, "");
        }
    }

    public async getTeamArchiveStatus(
        request: TeamsGetArchivedStatusRequest,
        defaultErrorMessage: string,
        onSuccess: (response: boolean) => void,
        onError: (errorMessage: string) => void
    ) {
        this.makeApiCall(
            get(
                "/api/teamsdmsintegration/GetTeamArchiveStatus?tenantId=" +
                    request.tenantId +
                    "&teamsId=" +
                    request.teamId
            ),
            defaultErrorMessage,
            onSuccess,
            onError
        );
    }

    public async getExternalTeamName(
        request: TeamsGetExternalTeamNameRequest,
        defaultErrorMessage: string,
        onSuccess: (response: string) => void,
        onError: (errorMessage: string, errorStatusCode: string) => void
    ) {
        this.makeApiCall(
            get(
                "/api/teamsdmsintegration/GetExternalTeamName?teamsId=" +
                    request.teamId +
                    "&externalTeamId=" +
                    request.externalTeamId +
                    "&tenantId=" +
                    request.tenantId
            ),
            defaultErrorMessage,
            onSuccess,
            onError
        );
    }

    public async getTeamsChannels(
        request: TeamsGetTeamsChannelsRequest,
        defaultErrorMessage: string,
        onSuccess: (response: FilingLocation[]) => void,
        onError: (errorMessage: string, errorStatusCode: string) => void
    ) {
        this.makeApiCall(
            post("/api/teamsdmsintegration/GetTeamsChannels", {
                TeamsId: request.teamId,
                ChannelId: request.channelId,
                RecentDocs: request.recentDocs,
                TenantId: request.tenantId,
                UserId: request.userId,
                ExternalTeamId: request.externalTeamId,
            }),
            defaultErrorMessage,
            onSuccess,
            onError
        );
    }

    public async getTeamChannels(
        request: TeamsGetTeamChannelsRequest,
        defaultErrorMessage: string,
        onSuccess: (response: IDropdownOption[]) => void,
        onError: (errorMessage: string) => void
    ) {
        this.makeApiCall(
            get(
                "/api/teamsdmsintegration/GetTeamChannels?teamsId=" +
                    request.teamId +
                    "&tenantId=" +
                    request.tenantId +
                    "&generalChannelId=" +
                    request.channelId +
                    "&spHostKVPrefix=" +
                    request.spHostKVPrefix
            ),
            defaultErrorMessage,
            onSuccess,
            onError
        );
    }
}
